import type { DropdownProps } from 'src/UIKit/components/Dropdown';
import { SxProps, Theme } from '@mui/material';
import React, { ReactElement, ReactNode, useCallback } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';
import Dropdown from 'src/UIKit/components/Dropdown';
import { SelectOption } from 'src/UIKit/components/types';

interface FormDropdownProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  fieldName: Path<T>;

  label?: ReactNode;
  placeholder?: string;
  helperText?: ReactNode;
  validation?: RegisterOptions;
  options: SelectOption<PathValue<T, Path<T>>, ReactNode>[];

  disabled?: boolean;
  size?: DropdownProps<string, unknown>['size'];
  displayEmpty?: boolean;
  variant?: "outlined" | "filled" | "standard";
  margin?: "dense" | "normal" | "none";
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

export const FormDropdown = <T extends FieldValues>(props: FormDropdownProps<T>): ReactElement | null => {
  const {
    form,
    fieldName,
    label,
    placeholder,
    helperText,
    validation,
    options,
    disabled,
    size,
    displayEmpty,
    margin = "normal",
    variant = "outlined",
    fullWidth = true,
    sx,
  } = props;

  const validateRequired = useCallback(
    (value: PathValue<T, Path<T>>) => {
      if (validation?.required) {
        const firstOption = options[0];
        const isEmpty =
          value === firstOption?.title && (firstOption?.value === null || firstOption?.value === undefined);

        if (isEmpty) {
          return String(validation.required);
        }
      }

      return true;
    },
    [validation, options]
  );

  return (
    <Controller
      name={fieldName}
      control={form.control as Control<FieldValues>}
      rules={{
        ...validation,
        validate: validateRequired,
      }}
      render={({ field, fieldState }) => {
        const { value, onChange, ...rest } = field;
        const { error } = fieldState;

        return (
          <Dropdown
            {...rest}
            label={label}
            placeholder={placeholder}
            value={value}
            options={options}
            onChange={(e) => onChange(e)}
            error={!!error}
            helperText={error?.message || helperText}
            required={!!validation?.required}
            disabled={disabled}
            size={size}
            displayEmpty={displayEmpty}
            variant={variant}
            fullWidth={fullWidth}
            margin={margin}
            sx={sx}
          />
        );
      }}
    />
  );
};
